import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { FC } from "react";
import { css } from "styled-components";

const enum fields {
    products = "products",
    productImage = "productImage",
    title = "title",
    downloadImage = "downloadImage",
    customCSS = "customCSS",
}

interface ProductCardFieldData {
    productImage: string;
    title: string;
    downloadImage: string;
}

interface ProductCardField {
    fields: ProductCardFieldData;
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.products]?: ProductCardField[];
        [fields.productImage]?: string;
        [fields.title]?: string;
        [fields.downloadImage]?: string;
        [fields.customCSS]?: string;
    };
}

export interface ProductDownloadCardStyles {
    productContainer?: InjectableCss;
    imageContainer?: InjectableCss;
    productImage?: InjectableCss;
    actionLink?: InjectableCss;
    actionIcon?: InjectableCss;
    title?: InjectableCss;
}

export const productDownloadCardStyles: ProductDownloadCardStyles = {
    productContainer: {
        css: css`
            text-align: center;
            height: 100%;
            width: 100%;
            margin-bottom: 0px !important;
        `,
    },
    imageContainer: {
        css: css`
            background-color: #dfe7ed;
            border-radius: 41px;
            position: relative;

            @media (max-width: 767px) {
                border-radius: 24px;
            }
        `,
    },
    productImage: {
        css: css`
            width: 100%;
            height: auto;
            will-change: opacity;
            transition: opacity;
            opacity: 1;
            display: block;
            border-radius: 41px;

            @media (max-width: 767px) {
                border-radius: 24px;
            }
        `,
    },
    actionLink: {
        css: css`
            position: absolute;
            top: 0;
            right: 0;
            background: #1e6e9b;
            width: 40px;
            height: 40px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            text-align: center;
            z-index: 1;

            @media (max-width: 767px) {
                width: 34px;
                height: 34px;
            }
        `,
    },
    actionIcon: {
        css: css`
            width: 22px;
            height: 22px;
            margin: auto;
            display: block;
        `,
    },
    title: {
        css: css`
            color: rgb(0, 0, 0);
            font-size: 16px;
            font-weight: 600;
            font-family: Poppins;
            line-height: 24px;
            margin: 12px 0 0;

            @media (max-width: 767px) {
                font-size: 14px;
                line-height: 20px;
            }
        `,
    },
};

const defaultCustomCss = `
    .product-container {
        text-align: center;
    }

    @media (max-width: 767px) {
        .product-container {
            margin-bottom: 18px !important;
        }
    }
`;

const getDownloadFileName = (url: string, fallback = "download") => {
    try {
        const parsedUrl = new URL(url, window.location.origin);
        const fileName = parsedUrl.pathname.split("/").pop();
        return fileName ? decodeURIComponent(fileName) : fallback;
    } catch {
        const fileName = url.split("?")[0].split("/").pop();
        return fileName ? decodeURIComponent(fileName) : fallback;
    }
};

const downloadImage = async (event: React.MouseEvent<HTMLAnchorElement>, link: HTMLAnchorElement) => {
    event.preventDefault();

    const fileName = link.download || getDownloadFileName(link.href, "download");

    try {
        const response = await fetch(link.href, { credentials: "same-origin" });
        if (!response.ok) {
            throw new Error(`Download failed: ${response.status}`);
        }

        const blobUrl = URL.createObjectURL(await response.blob());
        const downloadLink = document.createElement("a");
        downloadLink.href = blobUrl;
        downloadLink.download = fileName;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
        setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch (error) {
        console.error("Download error:", error);

        const linkUrl = new URL(link.href, window.location.href);
        const fallbackLink = document.createElement("a");
        fallbackLink.href = linkUrl.href;
        fallbackLink.download = fileName;
        document.body.appendChild(fallbackLink);
        fallbackLink.click();
        document.body.removeChild(fallbackLink);
    }
};

const ProductDownloadCard: FC<OwnProps> = ({ fields }) => {
    const sharedDownloadImage = fields.downloadImage || "/userfiles/Download.png";
    const products = Array.isArray(fields.products) && fields.products.length > 0
        ? fields.products.map(item => ({
              productImage: item?.fields?.productImage || "/userfiles/product-image.png",
              title: item?.fields?.title || "Product",
          }))
        : [
              {
                  productImage: fields.productImage || "/userfiles/product-image.png",
                  title: fields.title || "AJ",
              },
          ];

    const customCssWrapper = {
        css: css`
            ${fields.customCSS || defaultCustomCss}
        `,
    };
    

    return (
        <StyledWrapper {...customCssWrapper}>
                <GridContainer style={{padding: "15px"}}>
                {products.map((product, index) => {
                    const productImage = product.productImage || "/userfiles/product-image.png";
                    const productName = product.title || `Product ${index + 1}`;
                    return (
                        <GridItem width={[6,4,3,2,2]} key={`${productName}-${index}`}>
                        <StyledWrapper {...productDownloadCardStyles.productContainer}>
                            <StyledWrapper {...productDownloadCardStyles.imageContainer} className="image-container">
                                <img
                                    src={productImage}
                                    alt={productName || "product-image"}
                                    style={{
                                        width: "100%",
                                        height: "auto",
                                        display: "block",
                                        borderRadius: "41px",
                                    }}
                                />

                                <a
                                    href={productImage}
                                    download={getDownloadFileName(productImage)}
                                    onClick={event => downloadImage(event, event.currentTarget)}
                                    aria-label={`Download ${productName}`}
                                    style={{
                                        position: "absolute",
                                        top: "-2px",
                                        right: "-2px",
                                        background: "#1E6E9B",
                                        width: "38px",
                                        height: "38px",
                                        borderRadius: "50%",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        textAlign: "center",
                                    }}
                                >
                                    <img
                                        src={sharedDownloadImage}
                                        alt="Download"
                                        style={{
                                            width: "22px",
                                            height: "22px",
                                            margin: "auto",
                                            display: "block",
                                        }}
                                    />
                                </a>
                            </StyledWrapper>

                            <h5
                                style={{
                                    color: "rgb(0, 0, 0)",
                                    fontSize: "16px",
                                    fontWeight: 600,
                                    fontFamily: "Poppins",
                                    lineHeight: "24px",
                                    margin: "12px 0 0",
                                }}
                            >
                                {translate(productName)}
                            </h5>
                        </StyledWrapper></GridItem>
                       
                    );
                })}
                 </GridContainer>
        </StyledWrapper>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: ProductDownloadCard,
    definition: {
        group: "Basic",
        icon: "ax-image",
        fieldDefinitions: [
            {
                name: fields.downloadImage,
                displayName: "Download Icon",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
                tab: basicTab,
            },
            {
                name: fields.products,
                displayName: "Products",
                editorTemplate: "ListField",
                defaultValue: [],
                fieldType: "Translatable",
                getDisplay: (item: any) => item?.fields?.title || "Product",
                fieldDefinitions: [
                    {
                        name: fields.productImage,
                        displayName: "Product Image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        tab: basicTab,
                    },
                    {
                        name: fields.title,
                        displayName: "Product Name",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        tab: basicTab,
                    },
                ],
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                defaultValue: defaultCustomCss,
                tab: advancedTab,
            },
        ],
    },
};

export default widgetModule;
